import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { saveAs } from 'file-saver';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { AttachmentKind, AttachmentPreviewService, StatementAttachment } from '../../services/attachment-preview.service';

export interface DocumentViewerData {
  surveyId: number;
  questionId: number;
  attachments: StatementAttachment[];
  index: number;
}

const ZOOM_STEPS = [0.5, 0.67, 0.8, 1, 1.25, 1.5, 2, 2.5, 3];

@Component({
  selector: 'app-modal-document-viewer',
  templateUrl: './modal-document-viewer.component.html',
  styleUrls: ['./modal-document-viewer.component.scss'],
  standalone: false,
})
export class ModalDocumentViewerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('stage') stageRef?: ElementRef<HTMLDivElement>;

  index: number;
  kind: AttachmentKind = 'file';
  loading = false;
  failed = false;
  page = 1;
  pageCount = 1;
  zoom = 1;
  docxHtml = '';
  private pdf: PDFDocumentProxy | null = null;
  private imageUrl: string | null = null;
  private renderToken = 0;
  private resizeTimer: any;

  constructor(
    public dialogRef: MatDialogRef<ModalDocumentViewerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentViewerData,
    public preview: AttachmentPreviewService,
  ) {
    this.index = Math.min(Math.max(0, data.index), data.attachments.length - 1);
  }

  get viewable(): StatementAttachment[] {
    return this.data.attachments;
  }

  get current(): StatementAttachment {
    return this.data.attachments[this.index];
  }

  get canDownload(): boolean {
    return this.current?.type === 'FILE' && !this.current?.is_read_only;
  }

  get zoomPercent(): number {
    return Math.round(this.zoom * 100);
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.open(this.index));
  }

  ngOnDestroy(): void {
    this.renderToken++;
    clearTimeout(this.resizeTimer);
  }

  @HostListener('window:resize')
  onResize(): void {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.render(), 150);
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target?.tagName === 'INPUT') {
      return;
    }
    if (event.key === 'ArrowRight' || event.key === 'PageDown') {
      this.goToPage(this.page + 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
      this.goToPage(this.page - 1);
    } else if (event.key === '+' || event.key === '=') {
      this.zoomIn();
    } else if (event.key === '-') {
      this.zoomOut();
    }
  }

  open(index: number): void {
    if (index < 0 || index >= this.data.attachments.length) {
      return;
    }
    this.index = index;
    this.kind = this.preview.kind(this.current);
    this.pdf = null;
    this.imageUrl = null;
    this.docxHtml = '';
    this.failed = false;
    this.zoom = 1;
    this.page = 1;
    this.pageCount = 1;

    if (!this.preview.isViewableInApp(this.current)) {
      this.clearCanvas();
      return;
    }

    this.loading = true;
    const token = ++this.renderToken;
    const { surveyId, questionId } = this.data;
    const load = async () => {
      if (this.kind === 'pdf') {
        this.pdf = await this.preview.pdf(surveyId, questionId, this.current);
        this.pageCount = this.pdf.numPages;
        this.page = Math.min(this.preview.pageOf(this.current), this.pageCount);
      } else if (this.kind === 'image') {
        this.imageUrl = await this.preview.objectUrl(surveyId, questionId, this.current);
      } else if (this.kind === 'docx') {
        const mammoth = (window as any).mammoth;
        const blob = await this.preview.blob(surveyId, questionId, this.current);
        if (!mammoth) {
          throw new Error('Viewer not available');
        }
        const result = await mammoth.convertToHtml({ arrayBuffer: await blob.arrayBuffer() });
        this.docxHtml = result.value;
      }
    };

    load()
      .then(() => {
        if (token !== this.renderToken) {
          return;
        }
        this.loading = false;
        setTimeout(() => this.render());
      })
      .catch(() => {
        if (token !== this.renderToken) {
          return;
        }
        this.loading = false;
        this.failed = true;
      });
  }

  previousDocument(): void {
    this.open(this.index - 1);
  }

  nextDocument(): void {
    this.open(this.index + 1);
  }

  goToPage(page: number): void {
    if (this.kind !== 'pdf' || !this.pdf) {
      return;
    }
    const target = Math.min(Math.max(1, Math.round(page || 1)), this.pageCount);
    if (target !== this.page) {
      this.page = target;
      this.render();
      this.stageRef?.nativeElement.scrollTo({ top: 0 });
    }
  }

  zoomIn(): void {
    const next = ZOOM_STEPS.find((step) => step > this.zoom + 0.001);
    if (next) {
      this.zoom = next;
      this.render();
    }
  }

  zoomOut(): void {
    const previous = [...ZOOM_STEPS].reverse().find((step) => step < this.zoom - 0.001);
    if (previous) {
      this.zoom = previous;
      this.render();
    }
  }

  resetZoom(): void {
    this.zoom = 1;
    this.render();
  }

  async download(): Promise<void> {
    if (!this.canDownload) {
      return;
    }
    const blob = await this.preview.blob(this.data.surveyId, this.data.questionId, this.current);
    saveAs(blob, this.current.title || 'document');
  }

  openExternal(): void {
    if (this.current?.link && (this.current.type === 'LINK' || this.current.type === 'PUBMED')) {
      window.open(this.current.link, '_blank', 'noopener');
    }
  }

  blockContextMenu(event: MouseEvent): void {
    if (this.current?.is_read_only) {
      event.preventDefault();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  private async render(): Promise<void> {
    const canvas = this.canvasRef?.nativeElement;
    const stage = this.stageRef?.nativeElement;
    if (!canvas || !stage || this.loading || this.failed) {
      return;
    }
    const token = ++this.renderToken;
    const available = Math.max(200, stage.clientWidth - 48);

    try {
      if (this.kind === 'pdf' && this.pdf) {
        const page = await this.pdf.getPage(this.page);
        const base = page.getViewport({ scale: 1 });
        if (token !== this.renderToken) {
          return;
        }
        await this.preview.renderPdfPage(this.pdf, this.page, canvas, (available / base.width) * this.zoom);
      } else if (this.kind === 'image' && this.imageUrl) {
        const image = await this.preview.loadImage(this.imageUrl);
        if (token !== this.renderToken) {
          return;
        }
        const fit = Math.min(1, available / image.naturalWidth, (stage.clientHeight - 48) / image.naturalHeight);
        await this.preview.drawImage(this.imageUrl, canvas, Math.max(0.05, fit) * this.zoom);
      }
    } catch {
      if (token === this.renderToken) {
        this.failed = true;
      }
    }
  }

  private clearCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
